import type { SpecificityOptionKey } from '@massilia/queries'

import {
  useCreateBookingSpecificityMutation,
  useCreateSessionSpecificityMutation,
} from '@massilia/queries'
import { Button, Checkbox, Input, Label, RadioGroup, RadioGroupItem, Textarea } from '@massilia/ui'
import { Check, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface SpecificitySectionProps {
  bookingId?: string
  sessionId?: string
}

// 'ANIMAUX' est une clé UI uniquement : le payload envoie ANIMAL_SOUTE / ANIMAL_CABINE.
type UiOptionKey = 'ANIMAUX' | SpecificityOptionKey

const FORM_OPTIONS: { key: UiOptionKey; label: string }[] = [
  { key: 'AUTO', label: 'Auto' },
  { key: 'ASSISTANCE_CHAISE_ROULANTE', label: 'Assistance : Chaise Roulante' },
  { key: 'PREVENIR_HANDICAP', label: 'Prévenir handicap' },
  { key: 'RECONTACTER_AGENCE', label: 'Recontacter agence' },
  { key: 'DEMANDE_SIEGE', label: 'Demande de siège' },
  { key: 'BAGAGE_SUPPLEMENTAIRE', label: 'Bagage en plus' },
  { key: 'ANIMAUX', label: 'Animaux' },
  { key: 'UM_JEUNE_12_17', label: 'UM jeune 12-17 ans' },
  { key: 'TEXTE_LIBRE', label: 'Texte Libre' },
]

type HandicapKey =
  | 'HANDICAP_AUDITIF'
  | 'HANDICAP_AUTRE'
  | 'HANDICAP_COGNITIF'
  | 'HANDICAP_MOBILITE'
  | 'HANDICAP_VISUEL'

const HANDICAP_TYPES: { key: HandicapKey; label: string }[] = [
  { key: 'HANDICAP_MOBILITE', label: 'Mobilité réduite / fauteuil roulant' },
  { key: 'HANDICAP_VISUEL', label: 'Handicap visuel' },
  { key: 'HANDICAP_AUDITIF', label: 'Handicap auditif' },
  { key: 'HANDICAP_COGNITIF', label: 'Handicap cognitif' },
  { key: 'HANDICAP_AUTRE', label: 'Autre' },
]

type AnimalLocation = 'ANIMAL_CABINE' | 'ANIMAL_SOUTE'

export function SpecificitySection({ bookingId, sessionId }: SpecificitySectionProps) {
  const [checkedOptions, setCheckedOptions] = useState<Set<UiOptionKey>>(new Set())
  const [freeText, setFreeText] = useState('')
  const [freeTextError, setFreeTextError] = useState<null | string>(null)
  const [handicapTypes, setHandicapTypes] = useState<Set<HandicapKey>>(new Set())
  const [handicapOtherText, setHandicapOtherText] = useState('')
  const [handicapError, setHandicapError] = useState<null | string>(null)
  const [animalLocation, setAnimalLocation] = useState<AnimalLocation | null>(null)
  const [animalBreed, setAnimalBreed] = useState('')
  const [animalWeight, setAnimalWeight] = useState('')
  const [animalError, setAnimalError] = useState<null | string>(null)
  const [submitted, setSubmitted] = useState(false)

  const bookingMutation = useCreateBookingSpecificityMutation()
  const sessionMutation = useCreateSessionSpecificityMutation()

  const isPending = bookingMutation.isPending || sessionMutation.isPending

  const toggleOption = (key: UiOptionKey) => {
    setCheckedOptions((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
        if (key === 'TEXTE_LIBRE') setFreeTextError(null)
        if (key === 'PREVENIR_HANDICAP') {
          setHandicapTypes(new Set())
          setHandicapOtherText('')
          setHandicapError(null)
        }
        if (key === 'ANIMAUX') {
          setAnimalLocation(null)
          setAnimalBreed('')
          setAnimalWeight('')
          setAnimalError(null)
        }
      } else {
        next.add(key)
      }
      return next
    })
  }

  const toggleHandicapType = (key: HandicapKey) => {
    setHandicapTypes((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
        if (key === 'HANDICAP_AUTRE') setHandicapOtherText('')
      } else {
        next.add(key)
      }
      return next
    })
    if (handicapError) setHandicapError(null)
  }

  const handleSubmit = () => {
    if (checkedOptions.size === 0) return

    let hasError = false
    if (checkedOptions.has('PREVENIR_HANDICAP')) {
      if (handicapTypes.size === 0) {
        setHandicapError('Veuillez préciser le type de handicap')
        hasError = true
      } else if (handicapTypes.has('HANDICAP_AUTRE') && !handicapOtherText.trim()) {
        setHandicapError('Veuillez décrire le handicap')
        hasError = true
      }
    }
    if (checkedOptions.has('ANIMAUX') && !animalLocation) {
      setAnimalError("Veuillez indiquer si l'animal voyage en soute ou en cabine")
      hasError = true
    }
    if (checkedOptions.has('TEXTE_LIBRE') && !freeText.trim()) {
      setFreeTextError('Veuillez décrire vos besoins spécifiques')
      hasError = true
    }
    if (hasError) return

    const options: SpecificityOptionKey[] = []
    for (const key of checkedOptions) {
      if (key !== 'ANIMAUX') options.push(key)
    }
    if (checkedOptions.has('PREVENIR_HANDICAP')) options.push(...handicapTypes)
    if (checkedOptions.has('ANIMAUX') && animalLocation) options.push(animalLocation)

    // Détails des sous-formulaires sérialisés en texte libre (une ligne par sujet),
    // lisibles tels quels en back-office.
    const freeTextLines: string[] = []
    if (
      checkedOptions.has('PREVENIR_HANDICAP') &&
      handicapTypes.has('HANDICAP_AUTRE') &&
      handicapOtherText.trim()
    ) {
      freeTextLines.push(`Handicap (autre) : ${handicapOtherText.trim()}`)
    }
    if (checkedOptions.has('ANIMAUX') && animalLocation) {
      freeTextLines.push(
        `Animal en ${animalLocation === 'ANIMAL_SOUTE' ? 'soute' : 'cabine'} — race : ${
          animalBreed.trim() || 'non précisée'
        } — poids cage incluse : ${animalWeight.trim() || 'non précisé'}`
      )
    }
    if (checkedOptions.has('TEXTE_LIBRE') && freeText.trim()) {
      freeTextLines.push(freeText.trim())
    }
    const builtFreeText = freeTextLines.join('\n')

    const payload = {
      options,
      ...(builtFreeText ? { freeText: builtFreeText } : {}),
    }

    const onSuccess = () => {
      setSubmitted(true)
      toast.success('Spécificités envoyées avec succès')
    }

    const onError = () => {
      toast.error("Erreur lors de l'envoi des spécificités")
    }

    if (bookingId) {
      bookingMutation.mutate({ ...payload, bookingId }, { onError, onSuccess })
    } else if (sessionId) {
      sessionMutation.mutate({ ...payload, sessionId }, { onError, onSuccess })
    }
  }

  if (submitted) {
    return (
      <div className="border-gray-light-200 flex items-center gap-3 rounded-2xl border bg-white p-6">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center bg-success-subtle rounded-full">
          <Check className="h-4 w-4 text-success-foreground" />
        </div>
        <div>
          <div className="text-gray-light-900 text-sm font-semibold">Spécificités envoyées</div>
          <p className="text-gray-light-600 text-xs">
            Notre équipe a bien reçu vos demandes et vous recontactera si nécessaire.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="border-gray-light-200 flex flex-col gap-4 rounded-2xl border bg-white p-6">
      <div>
        <div className="text-gray-light-900 text-lg font-semibold">Spécificités</div>
        <p className="text-gray-light-600 mt-1 text-sm">
          Signalez-nous vos besoins spécifiques pour ce voyage. Votre demande sera transmise à notre
          équipe directement et celle-ci vous contactera rapidement.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {FORM_OPTIONS.map((option) => (
          <div className="flex flex-col gap-3" key={option.key}>
            <label className="flex cursor-pointer items-center gap-3">
              <Checkbox
                checked={checkedOptions.has(option.key)}
                onCheckedChange={() => toggleOption(option.key)}
              />
              <span className="text-gray-light-700 text-sm">{option.label}</span>
            </label>

            {option.key === 'PREVENIR_HANDICAP' && checkedOptions.has('PREVENIR_HANDICAP') && (
              <div className="border-gray-light-200 ml-7 flex flex-col gap-2 border-l pl-3">
                {HANDICAP_TYPES.map((type) => (
                  <label className="flex cursor-pointer items-center gap-3" key={type.key}>
                    <Checkbox
                      checked={handicapTypes.has(type.key)}
                      onCheckedChange={() => toggleHandicapType(type.key)}
                    />
                    <span className="text-gray-light-700 text-sm">{type.label}</span>
                  </label>
                ))}
                {handicapTypes.has('HANDICAP_AUTRE') && (
                  <Input
                    className="mt-1"
                    onChange={(e) => {
                      setHandicapOtherText(e.target.value)
                      if (handicapError) setHandicapError(null)
                    }}
                    placeholder="Précisez le type de handicap"
                    value={handicapOtherText}
                  />
                )}
                {handicapError && <p className="text-danger-foreground text-sm">{handicapError}</p>}
              </div>
            )}

            {option.key === 'ANIMAUX' && checkedOptions.has('ANIMAUX') && (
              <div className="border-gray-light-200 ml-7 flex flex-col gap-3 border-l pl-3">
                <RadioGroup
                  className="flex items-center gap-6"
                  onValueChange={(value) => {
                    if (value !== 'ANIMAL_SOUTE' && value !== 'ANIMAL_CABINE') return
                    setAnimalLocation(value)
                    if (animalError) setAnimalError(null)
                  }}
                  value={animalLocation ?? ''}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="animal-soute" value="ANIMAL_SOUTE" />
                    <Label className="text-gray-light-700 text-sm font-normal" htmlFor="animal-soute">
                      Soute
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="animal-cabine" value="ANIMAL_CABINE" />
                    <Label
                      className="text-gray-light-700 text-sm font-normal"
                      htmlFor="animal-cabine"
                    >
                      Cabine
                    </Label>
                  </div>
                </RadioGroup>
                <Input
                  onChange={(e) => setAnimalBreed(e.target.value)}
                  placeholder="Race de l'animal"
                  value={animalBreed}
                />
                <Input
                  onChange={(e) => setAnimalWeight(e.target.value)}
                  placeholder="Poids en kg (cage incluse)"
                  value={animalWeight}
                />
                {animalError && <p className="text-danger-foreground text-sm">{animalError}</p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {checkedOptions.has('TEXTE_LIBRE') && (
        <div className="flex flex-col gap-1">
          <Textarea
            className={`mt-1${freeTextError ? ' border-danger' : ''}`}
            onChange={(e) => {
              setFreeText(e.target.value)
              if (freeTextError) setFreeTextError(null)
            }}
            placeholder="Décrivez vos besoins spécifiques..."
            rows={3}
            value={freeText}
          />
          {freeTextError && (
            <p className="text-danger-foreground text-sm">{freeTextError}</p>
          )}
        </div>
      )}

      <div>
        <Button
          disabled={checkedOptions.size === 0 || isPending}
          onClick={handleSubmit}
          type="button"
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Envoyer
        </Button>
      </div>
    </div>
  )
}
